<template>
    <page :tabs="tabs" :busy="busy">
        <grid
            ref="grid"
            :columnDefs="columnDefs"
            :rowData="rowData"
            class="invoice-operations-grid"
            grid-theme="ag-theme-alpine-report"
            :isAutoWidth="true"
            :isSearch="false"
        >
            <div class="d-flex align-items-end justify-content-start">
                <date-range-picker
                    :range-options="predefinedRanges"
                    :value="dateRange"
                    :date-format="userUnicodeDateFormat"
                    @input="handleDaterangeChange"
                ></date-range-picker>
                <properties-search-multiselect
                    v-model="properties"
                    :properties="propertiesOptions"
                    variant="primary"
                    class="mr-1"
                />
                <responsive-button
                    :href="exportExcelHref"
                    @click="generatingExcelReportSuccessToast"
                    :icon="['fas', 'file-excel']"
                    variant="primary"
                ></responsive-button>
                <exportToPDFButton
                    :href="exportPdfHref"
                    :pdfCacheKey="pdfCacheKey"
                    :pageOrientation="pageOrientation"
                />

                <report-settings-modal :modalId="reportSettingsModalId" :initialValues="reportSettings" />
            </div>
        </grid>
    </page>
</template>

<script>
import API from '../../api';
import grid from '../generic/grid/grid.vue';
import page from '../generic/page/page.vue';
import format from 'date-fns/format';
import formSelect from '../generic/form-select/form-select.vue';
import { INVOICE_OPERATIONS_LIST_ENDPOINT, REPORT_ARRIVALS_PROPERTIES_ENDPOINT } from '../../endpoints';
import DateRangePicker, {
    ALL_DATES,
    LAST_MONTH,
    LAST_YEAR,
    nameToRange,
    THIS_MONTH,
    THIS_YEAR,
    TODAY,
    YESTERDAY,
} from '../generic/date-range-picker/date-range-picker.vue';
import { mapGetters } from 'vuex';
import ResponsiveButton from '../generic/responsive-button/responsive-button.vue';
import { escapeHTML } from '../../utils/strings';
import ReportSettingsModal, {
    DATE_PERIODS_INPUT,
    PROPERTIES_MULTI_SELECT,
} from './report-settings/report-settings-modal.vue';
import PropertiesSearchMultiselect from '../generic/properties-search-multiselect/properties-search-multiselect.vue';
import { defineAsyncComponent } from 'vue';

const exportToPDFButton = defineAsyncComponent(() =>
    import(
        /* webpackChunkName: "lx-export-to-pdf-button" */
        '../generic/export-to-pdf-button/export-to-pdf-button.vue'
    ))

export default {
    components: {
        page,
        grid,
        DateRangePicker,
        ResponsiveButton,
        exportToPDFButton,
        formSelect,
        ReportSettingsModal,
        PropertiesSearchMultiselect,
    },
    data() {
        return {
            busy: null,
            dateRange: nameToRange(THIS_MONTH),
            dateRequestFormat: 'yyyy-MM-dd',
            to_date: null,
            pageOrientation: 'landscape',
            predefinedRanges: [TODAY, YESTERDAY, THIS_MONTH, LAST_MONTH, THIS_YEAR, LAST_YEAR, ALL_DATES],
            rowData: [],
            from_date: null,
            tabs: [
                {
                    title: 'Invoice Operations Report',
                    route: '/app/reports/invoice-operations/report/',
                    active: true,
                },
            ],
            total: null,
            pdfCacheKey: null,
            reportSettingsModalId: 'report-settings-modal',
            properties: [],
            propertiesOptions: [],
        };
    },
    computed: {
        ...mapGetters('header', ['userUnicodeDateFormat', 'userCurrencySymbol']),
        columnDefs() {
            return [
                {
                    field: 'timestamp',
                    headerName: 'Timestamp',
                    type: 'dateTimeColumn',
                    cellStyle: { paddingRight: 0, paddingLeft: '12px' },
                },
                {
                    field: 'operation',
                    headerName: 'Operation',
                },
                {
                    field: 'meta_res_id',
                    headerName: 'Invoice #',
                    minWidth: 110,
                    maxWidth: 110,
                    cellRenderer: ({ data }) => (data && data.meta_res_id) || 'N/A',
                },
                {
                    field: 'guest',
                    headerName: 'Guest',
                },
                {
                    field: '',
                    headerName: 'Property/Room ID',
                    valueGetter: ({ data }) => data && data.properties.map((property) => property.id).join(' '),
                    minWidth: 150,
                    maxWidth: 150,
                },
                {
                    field: '',
                    headerName: 'Property/Room',
                    valueGetter: ({ data }) =>
                        data &&
                        data.properties
                            .map((property) => property.rental_property + (property.room ? ' — ' + property.room : ''))
                            .join('; '),
                    minWidth: 250,
                    cellRenderer: ({ data }) =>
                        data &&
                        data.properties
                            .map((property) =>
                                escapeHTML(property.rental_property + (property.room ? ' — ' + property.room : ''))
                            )
                            .join('<br/>'),
                    autoHeight: true,
                },
                {
                    field: 'from_date',
                    headerName: 'Arrival',
                    type: 'dateColumn',
                },
                {
                    field: 'to_date',
                    headerName: 'Departure',
                    type: 'dateColumn',
                },
                {
                    field: 'created',
                    headerName: 'Entered',
                    type: 'dateTimeColumn',
                    cellStyle: { paddingRight: 0, paddingLeft: '5px' },
                },
                {
                    field: 'user',
                    headerName: 'User',
                    cellStyle: { paddingRight: 0, paddingLeft: '5px' },
                },
                {
                    field: 'ip_address',
                    headerName: 'IP Address',
                    cellStyle: { paddingRight: 0 },
                },
                {
                    field: 'booking_method',
                    headerName: 'Booking Method',
                },
                {
                    field: 'note',
                    headerName: 'Note',
                },
            ];
        },

        endDateRequest() {
            return format(this.dateRange.to, this.dateRequestFormat);
        },
        exportPdfHref() {
            return `/app/reports/invoice-operations/invoice-operations/pdf/?from_date=${this.startDateRequest}&to_date=${this.endDateRequest}&properties=${this.propertiesRequest}`;
        },
        exportExcelHref() {
            return `/app/reports/invoice-operations/invoice-operations/excel/?from_date=${this.startDateRequest}&to_date=${this.endDateRequest}&properties=${this.propertiesRequest}`;
        },
        startDateRequest() {
            return format(this.dateRange.from, this.dateRequestFormat);
        },
        reportSettings() {
            return {
                name: 'Invoice Operations',
                report_sysname: 'INVOICE_OPERATIONS',
                settings: [
                    {
                        type: DATE_PERIODS_INPUT,
                        value: 'this_month',
                        predefinedRanges: this.predefinedRanges,
                        label: 'Period',
                        name: 'from_date',
                    },
                    {
                        type: PROPERTIES_MULTI_SELECT,
                        value: [],
                        options: this.propertiesOptions,
                        label: 'Only For Properties',
                        placeholder: 'All Properties',
                        name: 'properties',
                    },
                ],
            };
        },
        propertiesRequest() {
            return this.properties.map((property) => property.property_id).join(',');
        },
    },
    mounted() {
        this.fetchData();
        this.fetchProperties();
    },
    methods: {
        async fetchData() {
            try {
                this.busy = true;
                const { data } = await API.get(INVOICE_OPERATIONS_LIST_ENDPOINT(), {
                    params: {
                        from_date: this.startDateRequest,
                        to_date: this.endDateRequest,
                        properties: this.propertiesRequest,
                    },
                });
                this.rowData = data.items;
                this.pdfCacheKey = data.pdf_cache_key;
            } catch (e) {
                this.errorToast({ text: 'Could not fetch data' });
            } finally {
                this.busy = false;
            }
        },
        async fetchProperties() {
            try {
                const { data } = await API.get(REPORT_ARRIVALS_PROPERTIES_ENDPOINT());
                this.propertiesOptions = data;
            } catch (e) {
                this.errorToast({ text: 'Could not fetch arrivals' });
            }
        },
        handleDaterangeChange(value) {
            this.dateRange = { ...value };
            this.fetchData();
        },
    },
    watch: {
        properties() {
            this.fetchData();
        },
    },
};
</script>
